import { Link, createFileRoute } from "@tanstack/react-router";
import { BadgeCheck, Quote } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { PageSection } from "~/components/ui/page-section";
import { Skeleton } from "~/components/ui/skeleton";
import { StarRating } from "~/components/ui/star-rating";
import { cn } from "~/lib/cn";
import {
  useClientTestimonialsForCoach,
  useCoachAddedTestimonials,
  useSetClientTestimonialStatus,
} from "~/lib/queries/engagementTestimonialQuery";
import { formatDateOnly } from "~/lib/utils/timezone";

export const Route = createFileRoute("/_user/my-coaching/profile/testimonials")(
  {
    staticData: { title: "Client testimonials" },
    component: TestimonialsPage,
  },
);

interface StatusBadgeConfig {
  label: string;
  variant: "outline" | "success" | "cancelled";
  className?: string;
}

const STATUS_BADGES: Record<string, StatusBadgeConfig> = {
  pending: {
    label: "Awaiting review",
    variant: "outline",
    className: "border-amber-200 bg-amber-50 text-amber-800",
  },
  published: { label: "Live on profile", variant: "success" },
  hidden: { label: "Hidden", variant: "cancelled" },
};

function TestimonialsPage() {
  const { data: testimonials, isLoading } = useClientTestimonialsForCoach();
  const { data: coachAdded, isLoading: coachAddedLoading } =
    useCoachAddedTestimonials();
  const statusMutation = useSetClientTestimonialStatus();

  const handleStatus = (
    testimonialId: string,
    status: "published" | "hidden",
  ) => {
    statusMutation.mutate(
      { testimonialId, status },
      {
        onSuccess: () =>
          toast.success(
            status === "published"
              ? "Testimonial published to your profile"
              : "Testimonial hidden",
          ),
        onError: () => toast.error("Failed to update testimonial"),
      },
    );
  };

  if (isLoading || coachAddedLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <CoachAddedTestimonials testimonials={coachAdded ?? []} />
      <PageSection
        title="Verified client testimonials"
        description="Written by your clients at the end of their program. You choose what appears on your public profile — the words themselves are theirs and can't be edited."
      >
        {!testimonials || testimonials.length === 0 ? (
          <EmptyState
            icon={Quote}
            title="No verified testimonials yet"
            description="When a coaching program finishes, your client is invited to share a verified testimonial. They'll appear here for your review."
          />
        ) : (
          <ul className="space-y-4">
            {testimonials.map((t) => {
              const status = STATUS_BADGES[t.status] ?? STATUS_BADGES.pending;
              return (
                <li key={t.id}>
                  <Card>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <StarRating value={t.rating ?? 0} size="sm" />
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                          <BadgeCheck className="size-3.5" />
                          Verified client
                        </span>
                      </div>
                      <StatusBadge status={status} />
                    </div>
                    <blockquote className="text-text-secondary text-sm whitespace-pre-wrap">
                      {t.content}
                    </blockquote>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-text-tertiary text-xs">
                        — {t.author}
                        {t.submittedAt && `, ${formatDateOnly(t.submittedAt)}`}
                      </p>
                      <div className="flex gap-2">
                        {t.status !== "hidden" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={statusMutation.isPending}
                            onClick={() => handleStatus(t.id, "hidden")}
                          >
                            Hide
                          </Button>
                        )}
                        {t.status !== "published" && (
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={statusMutation.isPending}
                            onClick={() => handleStatus(t.id, "published")}
                          >
                            Publish to profile
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </PageSection>
    </div>
  );
}

function StatusBadge({
  status,
  className,
}: {
  status: StatusBadgeConfig;
  className?: string;
}): React.JSX.Element {
  return (
    <Badge variant={status.variant} className={cn(status.className, className)}>
      {status.label}
    </Badge>
  );
}

/**
 * The testimonials she added herself: typed on her offer page or saved from a
 * NOVA client story. They are live on her public profile; she edits, reorders
 * and removes them on the offer page.
 */
function CoachAddedTestimonials({
  testimonials,
}: {
  testimonials: { id: string; content: string; author: string }[];
}) {
  const offersLink = (
    <Link
      className="text-brand-700 underline hover:text-brand-900 hover:no-underline"
      to="/my-coaching/profile/offers"
    >
      your offer page
    </Link>
  );

  return (
    <PageSection
      title="Testimonials you added"
      description={
        <>
          Typed on your offer page or saved from a NOVA client story. They are
          live on your public profile. Edit, reorder or remove them on{" "}
          {offersLink}.
        </>
      }
    >
      {testimonials.length === 0 ? (
        <EmptyState
          icon={Quote}
          title="None added yet"
          description="Add a testimonial on your offer page, or turn a client's story into one with NOVA."
        />
      ) : (
        <ul className="space-y-4">
          {testimonials.map((t) => (
            <li key={t.id}>
              <Card>
                <StatusBadge
                  className="self-end"
                  status={STATUS_BADGES.published}
                />
                <blockquote className="text-text-secondary text-sm whitespace-pre-wrap">
                  {t.content}
                </blockquote>
                <p className="text-text-tertiary text-xs">— {t.author}</p>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </PageSection>
  );
}
